import { RotateCcw } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { CompetencyModal } from '../components/CompetencyModal';
import { competenciesApi } from '../competencyApi';
import type { Competency } from '../competencyTypes';

/**
 * The competency framework — what the personnel profile's Training/L&D panel is recorded
 * against.
 *
 * The standard management flow: index, create, profile, edit, delete. The anchor opens the
 * **profile**, not the edit form — every index page in this product is partnered with one, and
 * here the profile is where the count of L&D records already naming a competency is worth
 * reading before rewording it or retiring it.
 */
export function CompetenciesIndexPage() {
  const navigate = useNavigate();

  const [editing, setEditing] = useState<Competency | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState<{
    kind: 'deactivate' | 'activate';
    competency: Competency;
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchCompetencies = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      competenciesApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
        },
        signal,
      ),
    [],
  );

  const rowActions = useCallback(
    (competency: Competency): MenuItem[] => [
      {
        key: `edit-${competency.id}`,
        label: 'Edit',
        permission: 'competencies.update',
        onClick: () => setEditing(competency),
      },
      {
        key: `restore-${competency.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'competencies.delete',
        disabledReason: competency.isActive ? 'This competency is already active.' : null,
        onClick: () => setConfirming({ kind: 'activate', competency }),
      },
    ],
    [],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'deactivate') {
        await competenciesApi.remove(confirming.competency.id);
      } else {
        await competenciesApi.restore(confirming.competency.id);
      }

      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0),
      );
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<Competency>
        module="competencies"
        // Reached through Personnel → Settings, so Settings links: that crumb is the way back.
        breadcrumb={[
          'Human Resources',
          'Personnel',
          { label: 'Settings', to: '/personnel/settings' },
          'Competencies',
        ]}
        columns={COLUMNS}
        facets={[{ key: 'status', label: 'Status' }]}
        auditEntityTypes={['Competency']}
        rowKey={(competency) => competency.id}
        fetch={fetchCompetencies}
        reloadKey={reloadKey}
        searchPlaceholder="Search competencies"
        emptyMessage="No competencies yet. Add the ones this organization's framework names."
        onCreate={() => setCreating(true)}
        createPermission="competencies.create"
        // The anchor opens the profile; Edit is in the row menu. The read key, not the write
        // one — opening a record is not changing it.
        onRowOpen={(competency) => navigate(`/personnel/settings/competencies/${competency.id}`)}
        openPermission="competencies.read"
        rowActions={rowActions}
        onDelete={(competency) => setConfirming({ kind: 'deactivate', competency })}
        deletePermission="competencies.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(competency) =>
          competency.isActive ? null : 'This competency is already deleted.'
        }
        onClearData={() => competenciesApi.clear()}
        clearDataPermission="competencies.clear-data"
        otherInfo={(competency) => competency}
      />

      {creating || editing !== null ? (
        <CompetencyModal
          open
          editing={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'deactivate' ? 'Delete competency' : 'Restore competency'}
          danger={confirming.kind === 'deactivate'}
          // Both safeguards on the destructive half only. Restoring needs no ceremony.
          requireConfirm={confirming.kind === 'deactivate'}
          requireCredential={confirming.kind === 'deactivate'}
          loading={running}
          id={`competency-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'deactivate'
                  ? `"${confirming.competency.name}" stops being offered on the Training/L&D form.`
                  : `"${confirming.competency.name}" is offered again on the Training/L&D form.`}
              </p>

              {confirming.kind === 'deactivate' && confirming.competency.trainingCount > 0 ? (
                <p>
                  {confirming.competency.trainingCount} L&amp;D record
                  {confirming.competency.trainingCount === 1 ? '' : 's'} already name
                  {confirming.competency.trainingCount === 1 ? 's' : ''} it. Those records keep it
                  and still state it — retiring a competency does not rewrite what was recorded.
                </p>
              ) : null}

              <p>Nothing is erased — a deleted competency can be restored from this list.</p>

              {error ? (
                <p
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700"
                >
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<Competency>[] = [
  {
    // The name is the anchor: a competency is referred to by its word, never by its position.
    key: 'name',
    header: 'Competency',
    width: 260,
    sortable: true,
    render: (competency) => <TextCell value={competency.name} />,
  },
  {
    key: 'category',
    header: 'Group',
    width: 150,
    sortable: true,
    summary: true,
    render: (competency) => <TextCell value={competency.category} />,
  },
  {
    key: 'description',
    header: 'Description',
    width: 360,
    flex: true,
    summary: true,
    render: (competency) => <TextCell value={competency.description} />,
  },
  {
    // The stored sort value, under a heading that says so: a POSITION is only correct with the
    // whole ladder in hand, and a paged table never has it.
    key: 'sortOrder',
    header: 'Order',
    width: 90,
    align: 'right',
    sortable: true,
    render: (competency) => <TextCell value={String(competency.sortOrder)} mono numeric />,
  },
  {
    key: 'trainingCount',
    header: 'L&D records',
    width: 120,
    align: 'right',
    sortable: true,
    summary: true,
    // The count is of personnel records, which is another module's data — the competency's own
    // fields beside it are this module's and stay ungated.
    permission: 'personnel.read',
    render: (competency) => <TextCell value={String(competency.trainingCount)} mono numeric />,
  },
  {
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    render: (competency) =>
      competency.isActive ? (
        <StatusText tone="active">Active</StatusText>
      ) : (
        <StatusText tone="inactive">Deleted</StatusText>
      ),
  },
];
